import { useCallback, useRef } from 'react';
import type {
  ReportAnalyzeParams,
  ReportAnalyzeResult,
  MarkdownAnalyzeParams,
  MarkdownAnalyzeResult,
  MarkdownNameAnalyzeParams,
  MarkdownNameAnalyzeResult,
} from '../types';
import { parseReportUrl } from '../utils/parseReportUrl';

type AnalyzeCallbacks = {
  reportAnalyze?: (p: ReportAnalyzeParams) => Promise<ReportAnalyzeResult>;
  markdownAnalyze?: (p: MarkdownAnalyzeParams) => Promise<MarkdownAnalyzeResult>;
  markdownNameAnalyze?: (p: MarkdownNameAnalyzeParams) => Promise<MarkdownNameAnalyzeResult>;
  reportNameResolve?: (p: {
    url: string;
    nodeKey: string;
    nodeType: string;
  }) => Promise<{ name: string; url: string }>;
};

// analyzeReport 的四态结果：上层据 status 决定「填模块 / 清空 / 不动」。
// 关键是把 stale（token 被新解析顶替，不能清空）与 failed（接口真异常，要清空）分开。
export type ReportAnalyzeOutcome =
  | { status: 'unconfigured' }
  | { status: 'stale' }
  | { status: 'failed' }
  | { status: 'success'; modules: ReportAnalyzeResult['modules'] };

// resolveReportName 的四态结果（同 analyze 思路，把 skip/stale/failed 区分开）：
//   - skip：未配 callback 或非 http(s) URL → 不该解析，上层不做任何处理（不报错、不清空）
//   - stale：token 被新解析顶替（切 agent / 槽位变化 / 重触发）→ 丢弃，不污染相邻 chip
//   - failed：接口真异常 → 上层清空该 slot + 弹 message 报错
//   - success：拿到报告名
export type ResolveNameOutcome =
  | { status: 'skip' }
  | { status: 'stale' }
  | { status: 'failed' }
  | { status: 'success'; name: string; url: string };

export function useReportAnalyze(cb: AnalyzeCallbacks) {
  const tokens = useRef<Record<string, number>>({});
  // name resolve 用独立 token 池：与 analyze 解耦，互不作废（V1 parity —
  // analyze 早退/重触发不应让 in-flight name resolve 失效）。
  const nameTokens = useRef<Record<string, number>>({});

  const analyzeReport = useCallback(
    async (key: string, url: string): Promise<ReportAnalyzeOutcome> => {
      if (!cb.reportAnalyze) return { status: 'unconfigured' };
      const token = (tokens.current[key] ?? 0) + 1;
      tokens.current[key] = token;
      try {
        const r = await cb.reportAnalyze(parseReportUrl(url));
        // stale：解析返回前用户又触发了新解析（token 被顶替）→ 丢弃本次结果，
        // 不能当失败处理（否则会清掉用户刚粘的新值）。
        if (tokens.current[key] !== token) return { status: 'stale' };
        return { status: 'success', modules: r.modules };
      } catch {
        // 接口异常 = 真失败：上层据此清空（用户诉求「解析失败回到空框」）。
        // stale 守卫放在 catch 外不可行，故 catch 内也判一次 token。
        if (tokens.current[key] !== token) return { status: 'stale' };
        return { status: 'failed' };
      }
    },
    [cb.reportAnalyze],
  );

  const analyzeMarkdown = useCallback(
    async (key: string, text: string) => {
      if (!cb.markdownAnalyze) return null;
      const token = (tokens.current[key] ?? 0) + 1;
      tokens.current[key] = token;
      try {
        const r = await cb.markdownAnalyze({ text });
        if (tokens.current[key] !== token) return null;
        return r;
      } catch {
        return null;
      }
    },
    [cb.markdownAnalyze],
  );

  const analyzeMarkdownName = useCallback(
    async (key: string, text: string) => {
      if (!cb.markdownNameAnalyze) return null;
      const token = (tokens.current[key] ?? 0) + 1;
      tokens.current[key] = token;
      try {
        const r = await cb.markdownNameAnalyze({ text });
        if (tokens.current[key] !== token) return null;
        return r;
      } catch {
        return null;
      }
    },
    [cb.markdownNameAnalyze],
  );

  const resolveReportName = useCallback(
    async (key: string, url: string, nodeType: string): Promise<ResolveNameOutcome> => {
      if (!cb.reportNameResolve) return { status: 'skip' };
      const trimmed = (url || '').trim();
      if (!/^https?:\/\//i.test(trimmed)) return { status: 'skip' };
      const token = (nameTokens.current[key] ?? 0) + 1;
      nameTokens.current[key] = token;
      try {
        const r = await cb.reportNameResolve({ url: trimmed, nodeKey: key, nodeType });
        // 切 agent / chip 槽位变化 / 重触发时 token 失效 → 丢弃，避免污染相邻 chip（V1 parity）
        if (nameTokens.current[key] !== token) return { status: 'stale' };
        return { status: 'success', name: r.name, url: r.url || trimmed };
      } catch {
        // 接口异常 = 真失败：上层清空该 slot + 弹 message。stale 守卫放 catch 外不可行，故内再判一次。
        if (nameTokens.current[key] !== token) return { status: 'stale' };
        return { status: 'failed' };
      }
    },
    [cb.reportNameResolve],
  );

  return { analyzeReport, analyzeMarkdown, analyzeMarkdownName, resolveReportName };
}
